import { action, computed, observable } from 'mobx'
import type { Localization, Address, AddressDetails, OrderTypeFlow, LocalizationDependencies, Configuration} from "./types";
import { FlowStep, LocalizationFlow , OrderType } from "./types";

export default class LocalizationStore implements Localization {
	constructor(dependencies: LocalizationDependencies) {
		this.dependencies = dependencies
	}

    private readonly dependencies: LocalizationDependencies

    config = () => 
        // must be replaced by the content of appParams.features.localization
         this.dependencies.config() || defaultLocalization
    

    get = () => this.dependencies.get()

    @observable actualFlow: LocalizationFlow | null = this.get()

    @observable processingFlow: LocalizationFlow | null = null

    @observable editing = false

    @observable orderTypeFlows: OrderTypeFlow[] = []

    @action setOrderTypeFlows = (orderTypeFlows: OrderTypeFlow[]) => {
        this.orderTypeFlows = orderTypeFlows
    }

    @observable addressDetailsTypes: AddressDetails[] = []

    @action setAddressDetailsTypes = (addressDetailsTypes: AddressDetails[]) => {
        this.addressDetailsTypes = addressDetailsTypes
    }

    @observable savedAddress: Address[] = []

    @action addToSavedAddress = (address: Address) => {
        this.savedAddress = [...this.savedAddress, address]
    }

    @action removeSavedAddress = (addressId: string) => {
        this.savedAddress = [...this.savedAddress.filter(sa => sa.id !== addressId)]
    }

    @observable started = false

    @observable currentStep: FlowStep | null = null

    @computed get alreadyLocalized(): boolean {
        return this.actualFlow?.address !== null && this.actualFlow?.address !== undefined && this.actualFlow?.store !== null && this.actualFlow?.store !== undefined
    }

    availableStores = async() => {
        if(this.actualFlow?.address) {
            const orderType = this.actualFlow?.orderTypeFlow?.orderType
            const stores = await this.dependencies.getStores(this.actualFlow.address.formatted, this.actualFlow.address.location.lat, this.actualFlow.address.location.lng)

            if(orderType && stores.length > 0) {
                return stores.filter(
                    ({ availability }) =>
                        availability?.[orderType]?.availableForAddress && (availability.orderOnline || availability.preOrder)
                ) as []
            }
        }

        return []
    }

    @action start = async() => {
        this.started = true

        // Instanciate the flow
        this.processingFlow = new LocalizationFlow()

        // if config skipOrderTypeSelection is true and only one order type flow is available
        // we force the order type selection and move to the next step
        if(this.skipOrderTypeSelection()) {
            this.processingFlow.setOrderTypeFlow(this.orderTypeFlows[0])
            this.nextStep()
            return
        }

        this.currentStep = FlowStep.OrderTypeSelection
    }

    skipOrderTypeSelection = (): boolean => {
        const filteredOrderTypeFlows = this.getFilteredOrderTypeFlows()

        // fetch the orderTypeFlows, if not in cache already
        if(this.orderTypeFlows.length === 0) {
            this.setOrderTypeFlows(filteredOrderTypeFlows)
        }

        return this.config().canSkipOrderTypeSelection && this.orderTypeFlows.length === 1
    }

    getFilteredOrderTypeFlows = () => {
        const deliveryEnabled = this.dependencies.deliveryEnabled()
        const pickupEnabled = this.dependencies.pickupEnabled()

        // Filter the order type flows
        // if deliveryEnabled : requireAddress has to be true 
        // if pickEnabled : requireAddress has to be false 
        return this.config().orderTypeFlows.filter((dotf) => (deliveryEnabled && dotf.requireAddress) || (pickupEnabled && !dotf.requireAddress))
    }

    @action abort = () => {
        this.started = false
        this.processingFlow = null
        this.editing = false
        this.currentStep = null
    }

    @action end = async() => {
        this.started = false
        this.currentStep = null

        if(this.processingFlow?.store) {
            const response = await this.dependencies.initSessionAndLoadMenu(this.processingFlow.store.id)
            if(response !== false) {
                const flow = new LocalizationFlow()
                flow.setOrderTypeFlow(this.processingFlow.orderTypeFlow!)
                flow.setAddress(this.processingFlow.address!)
                flow.setStore(this.processingFlow.store!)
                this.actualFlow = flow

                this.dependencies.save(flow!)
            }

            // If processing flow store is not available and an actual flow store is already set (ex, durimg editing), 
            // we re-init the session of the previous store
            else if(this.actualFlow?.store?.id) {
                await this.dependencies.initSession(this.actualFlow.store.id)
            }
        }

        this.processingFlow = null
    }

    @action edit = () => {
        if(!this.actualFlow) {
            console.error('error edit: actualFlow is null')
            return
        }

        this.started = true
        this.editing = true
        this.currentStep = null

        this.processingFlow = new LocalizationFlow()
        this.processingFlow.setOrderTypeFlow(this.actualFlow.orderTypeFlow!)
        this.processingFlow.setAddress(this.actualFlow.address!)
        this.processingFlow.setStore(this.actualFlow.store!)
        this.processingFlow.setOrderTypeFlowDisplayAvailableStores(true)
    }

    @action canGoToStep = (step: FlowStep): boolean => {
        if(this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
            return false
        }

        return this.processingFlow.orderTypeFlow.steps.some((s => s === step))
    }

    @action goToStep = (step: FlowStep) => {
        if(this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
            return
        }

        const stepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s => s === step))
        this.currentStep = this.processingFlow.orderTypeFlow.steps[stepIndex]
    }

    @action nextStep = () => {
        if(this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
            return
        }

        // if we are at the start of the localization flow (order type view), move to the first step
        if(this.currentStep === FlowStep.OrderTypeSelection) {
            this.currentStep = this.processingFlow.orderTypeFlow.steps[0]
            return
        }

        const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s => s === this.currentStep))
        
        // if current step is the last step
        if(modalCurrentStepIndex === this.processingFlow.orderTypeFlow.steps.length - 1) {
            this.end()
            return
        }

        // move to the next step
        this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex + 1]
    }

    @action previousStep = () => {
        if(this.processingFlow?.orderTypeFlow === null || this.processingFlow?.orderTypeFlow === undefined) {
            return
        }

        const modalCurrentStepIndex = this.processingFlow.orderTypeFlow.steps.findIndex((s => s === this.currentStep))

        // if current step is the first step, return to the start of the localization flow (order type view)
        if(modalCurrentStepIndex === 0) {

            // execpt if skipOrderTypeSelection is true
            if(this.skipOrderTypeSelection()) {

                // and editing is active, we set to null the currentStep
                if(this.editing) {
                    this.currentStep = null
                }

                // else, we abort the flow
                else {
                    this.abort()
                }
            }

            // Else we set OrderTypeSelection as currentStep
            else {
                this.currentStep = FlowStep.OrderTypeSelection
            }

            return
        }

        // move to the previous step
        this.currentStep = this.processingFlow.orderTypeFlow.steps[modalCurrentStepIndex - 1]
    }

    @action goToLocalizedMenu = async() => {
        // init a new session before redirecting, in case of an order-lock is active
        const result = await this.dependencies.initSession()
        if(result === '') {
            return
        }

        // handle the menu redirection here...
        await this.dependencies.goToLocalizedMenu()
    }

    @action getLocalizedMenuPath = () => this.dependencies.getLocalizedMenuPath()

    @action getValidateAddress = () => {
        if(this.actualFlow?.address) {
            return this.dependencies.getValidateAddress(this.actualFlow.address)
        }
    }

    @action forceLocalizationToStore = async(storeId: string) => {
        const store = await this.dependencies.getStoreDetailsById(storeId)
        this.dependencies.checkPickupAvailabilityByStore(store)

        const filteredOrderTypeFlows = this.getFilteredOrderTypeFlows()
        const pickupOrderTypeFlow = filteredOrderTypeFlows.find((otf) => otf.orderType === OrderType.Pickup)

        // If pickup exists in the available order type flow
        if(pickupOrderTypeFlow) {
            const flow = new LocalizationFlow()
            flow.setOrderTypeFlow(pickupOrderTypeFlow)
            flow.setAddress({
                formatted: store.location.address,
                location: {
                    lat: store.location.coordinates.lat,
                    lng: store.location.coordinates.lng
                },
                id: null,
                details: null,
                components: []
            })
            flow.setStore({
                id: store.id,
                name: store.title,
                address: store.location.address
            })

            this.actualFlow = flow

            // handle the menu redirection here...
            if(this.actualFlow?.store) {
                await this.dependencies.initSessionAndLoadMenu(this.actualFlow.store.id)
            }

            this.dependencies.save(this.actualFlow!)
        }
    }
}

const defaultOrderTypeFlows = [
    {
        orderType: OrderType.Delivery,
        orderTypeTranslationKey: 'delivery',
        steps: [
            FlowStep.AddressSelection,
            FlowStep.AddressDetailsSelection,
            FlowStep.StoreOnMapSelection
        ],
        requireAddress: true
    },
    {
        orderType: OrderType.Pickup,
        orderTypeTranslationKey: 'peakup',
        steps: [
            FlowStep.StoreOnMapSelection
        ],
        requireAddress: false
    }
]

const defaultLocalization: Configuration = {
    orderTypeFlows: defaultOrderTypeFlows,
    canSkipOrderTypeSelection: true,
    displayToMenuButton: true,
    enabled: true
}
